import React, { useState, useMemo } from 'react';
import { Printer, Bluetooth, Eye, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { printThermalReceipt, previewThermalReceipt } from '@/utils/thermalPrinter';
import { getPrinterInstance } from '@/utils/bluetoothPrinter';
import { useToast } from '@/components/ui/use-toast';

const PrintEntregaButtons = ({ entrega, disabled = false }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [printStatus, setPrintStatus] = useState('idle');
  const [currentAction, setCurrentAction] = useState('');

  // Instancia compartida de la impresora Bluetooth
  const bluetoothPrinter = useMemo(() => getPrinterInstance(), []);

  const handlePrintThermal = async () => {
    if (!entrega) {
      toast({
        title: 'Error',
        description: 'No hay datos de entrega para imprimir',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    setCurrentAction('thermal');
    setPrintStatus('printing');
    
    try {
      await printThermalReceipt(entrega);
      setPrintStatus('success');
      toast({
        title: 'Impresión iniciada',
        description: 'El recibo se está imprimiendo'
      });
      setTimeout(() => {
        setPrintStatus('idle');
        setCurrentAction('');
      }, 2000);
    } catch (error) {
      console.error('Error al imprimir recibo térmico:', error);
      setPrintStatus('error');
      toast({
        title: 'Error',
        description: 'No se pudo imprimir el recibo',
        variant: 'destructive'
      });
      setTimeout(() => {
        setPrintStatus('idle');
        setCurrentAction('');
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  const handlePrintBluetooth = async () => {
    if (!entrega) {
      toast({
        title: 'Error',
        description: 'No hay datos de entrega para imprimir',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    setCurrentAction('bluetooth');
    setPrintStatus('connecting');
    
    try {
      if (!bluetoothPrinter.isConnected()) {
        toast({
          title: 'Conectando...',
          description: 'Selecciona tu impresora Bluetooth'
        });
        await bluetoothPrinter.connect();
      }

      setPrintStatus('printing');
      toast({
        title: 'Imprimiendo...',
        description: 'Enviando datos a la impresora'
      });

      await bluetoothPrinter.printEntregaReceipt(entrega);
      
      setPrintStatus('success');
      toast({
        title: 'Impresión exitosa',
        description: 'El recibo se imprimió correctamente'
      });
      setTimeout(() => {
        setPrintStatus('idle');
        setCurrentAction('');
      }, 2000);
      
    } catch (error) {
      console.error('Error al imprimir por Bluetooth:', error);
      setPrintStatus('error');
      toast({
        title: 'Error',
        description: error.message || 'No se pudo imprimir por Bluetooth',
        variant: 'destructive'
      });
      setTimeout(() => {
        setPrintStatus('idle');
        setCurrentAction('');
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!entrega) {
      toast({
        title: 'Error',
        description: 'No hay datos de entrega para previsualizar',
        variant: 'destructive'
      });
      return;
    }

    setCurrentAction('preview');
    try {
      previewThermalReceipt(entrega);
      setPrintStatus('success');
      setTimeout(() => {
        setPrintStatus('idle');
        setCurrentAction('');
      }, 1500);
    } catch (error) {
      console.error('Error al generar vista previa:', error);
      setPrintStatus('error');
      toast({
        title: 'Error',
        description: 'No se pudo generar la vista previa',
        variant: 'destructive'
      });
      setTimeout(() => {
        setPrintStatus('idle');
        setCurrentAction('');
      }, 2000);
    }
  };

  const getButtonVariant = (action) => {
    if (currentAction === action) {
      switch (printStatus) {
        case 'success':
          return 'default';
        case 'error':
          return 'destructive';
        default:
          return 'outline';
      }
    }
    return 'outline';
  };

  const getButtonIcon = (action) => {
    if (currentAction === action && loading) {
      return <Loader2 className="h-4 w-4 animate-spin" />;
    }
    if (currentAction === action && printStatus === 'success') {
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    }
    if (currentAction === action && printStatus === 'error') {
      return <AlertTriangle className="h-4 w-4 text-red-600" />;
    }

    switch (action) {
      case 'thermal':
        return <Printer className="h-4 w-4" />;
      case 'bluetooth':
        return <Bluetooth className="h-4 w-4" />;
      case 'preview':
        return <Eye className="h-4 w-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="flex gap-2">
      {/* Botón Impresora Térmica */}

      {/* Botón Impresora Bluetooth */}
      <Button
        variant={getButtonVariant('bluetooth')}
        size="sm"
        onClick={handlePrintBluetooth}
        disabled={disabled || loading}
        title="Imprimir vía Bluetooth"
      >
        {getButtonIcon('bluetooth')}
        <span className="ml-2">Imprimir</span>
      </Button>

      {/* Botón Vista Previa */}
      <Button
        variant={getButtonVariant('preview')}
        size="sm"
        onClick={handlePreview}
        disabled={disabled || loading}
        title="Generar vista previa"
      >
        {getButtonIcon('preview')}
        <span className="ml-2">Vista Previa</span>
      </Button>
    </div>
  );
};

export default PrintEntregaButtons;
